import { defineStore } from 'pinia'
import type { Question, QuestionConfiguration, Step, Document } from '@/questionnaire/types'
import questionConfigurationsService from '../services/questionConfigurationsService'
import * as questionsService from '../services/questionsService'
import type { TemplateVariable, TemplateVariableValue } from '../types'
import templateVariablesService from '../services/templateVariablesService'
import stepsService from '../services/stepsService'
import { fetchDocument } from '@/services/documentsService'
import { errorNotification } from '@/utils/notifications'

export type QuestionnaireStore = {
  steps: Step[]
  questions: Record<string, Question>
  questionConfigurations: Record<string, QuestionConfiguration>
  templateVariables: Record<string, TemplateVariable>
  templateVariableValues: Record<string, TemplateVariableValue>
  document: Document
  loaded: boolean
}

export const useQuestionnaireStore = defineStore('questionnaire', {
  state: (): QuestionnaireStore => ({
    steps: [],
    questions: {},
    questionConfigurations: {},
    templateVariables: {},
    templateVariableValues: {} as Record<string, TemplateVariableValue>,
    document: {} as Document,
    loaded: false,
  }),
  getters: {
    getTemplateVariable:
      state =>
      (template_variable_code: string): TemplateVariable => {
        const templateVariable = state.templateVariables[template_variable_code]

        if (templateVariable) {
          return templateVariable
        } else {
          return { code: template_variable_code, label: template_variable_code, input_type: 'text', default: '' }
        }
      },
    getTemplateVariableValue(): (_template_variable_code: string) => TemplateVariableValue {
      return (template_variable_code: string): TemplateVariableValue => {
        const templateVariable = this.getTemplateVariable(template_variable_code)

        if (!templateVariable) {
          return undefined
        }

        let value = this.templateVariableValues[template_variable_code]

        if (value === undefined || value === null) {
          value = templateVariable.default
        }

        return value
      }
    },
    // return the template variable key. The key combines the code and the value if it is a singleselect, multiselect or boolean
    getTemplateVariableKey() {
      return (template_variable_code: string): string => {
        const templateVariable = this.getTemplateVariable(template_variable_code)
        const value = this.getTemplateVariableValue(template_variable_code)

        if (!templateVariable || value === undefined) {
          return template_variable_code
        }

        if (templateVariable.input_type === 'singleselect' || templateVariable.input_type === 'multiselect') {
          return `${template_variable_code}-${value}`
        }

        return template_variable_code
      }
    },
  },
  actions: {
    resetStore() {
      this.$state = {
        ...this.$state,
        steps: [],
        questions: {},
        questionConfigurations: {},
        templateVariables: {},
        templateVariableValues: {} as Record<string, TemplateVariableValue>,
        document: {} as Document,
        loaded: false,
      }
    },

    updateDocumentState(newDocumentObjToUpdate: Document) {
      this.$patch({
        document: {
          ...this.document,
          ...newDocumentObjToUpdate,
        },
      })
    },

    async fetchData(documentId: string) {
      return await this.getDocument(documentId).then(async ({ template_id, template_variable_values }) => {
        if (!template_id || !template_variable_values) {
          return { documentTemplateId: undefined }
        }

        await Promise.all([
          stepsService.fetchSteps(template_id),
          questionsService.fetchAll(template_id),
          questionConfigurationsService.fetchQuestionConfigurations(template_id),
          templateVariablesService.fetchTemplateVariables(template_id),
        ])
          .then(async ([_steps, _questions, _questionConfigurations, templateVariables]) => {
            const thePatch: {
              document?: Document
              templateVariableValues?: Record<string, TemplateVariableValue>
            } = {}

            const variable_value_map: { [key: string]: string } = {}
            const template_variable_values_temp = Object.fromEntries(
              (template_variable_values || []).map(e => [e.template_variable_id, e.content])
            )

            if (Array.isArray(template_variable_values)) {
              Object.values(templateVariables).forEach(({ id, code }) => {
                if (id && code) {
                  variable_value_map[code] = <string>template_variable_values_temp[id]
                }
              })
              thePatch.templateVariableValues = variable_value_map
            }

            await Promise.all([
              stepsService.fetchSteps(template_id),
              questionsService.fetchAll(template_id),
              questionConfigurationsService.fetchQuestionConfigurations(template_id),
              templateVariablesService.fetchTemplateVariables(template_id),
            ]).then(async ([steps, questions, questionConfigurations, templateVariables]) => {
              const thePatch: {
                document?: Record<string, Document>
                templateVariableValues?: Record<string, TemplateVariableValue>
              } = {}

              const variable_value_map: { [key: string]: string } = {}
              const template_variable_values_temp = Object.fromEntries(
                (template_variable_values || []).map(e => [e.template_variable_id, e.content])
              )

              if (Array.isArray(template_variable_values)) {
                Object.values(templateVariables).forEach(({ id, code }) => {
                  if (id && code) {
                    variable_value_map[code] = <string>template_variable_values_temp[id]
                  }
                })
                thePatch.templateVariableValues = variable_value_map
              }

              this.$patch({
                steps,
                questions,
                questionConfigurations,
                templateVariables,
                ...thePatch,
                loaded: true,
              })
            })

            return { documentTemplateId: template_id }
          })
          .catch(error => {
            errorNotification(error.response.data.error)
          })
      })
    },

    async getDocument(documentId: string) {
      return await fetchDocument(documentId).then(({ document }) => {
        if (document.id) {
          this.$patch({
            document: document as Document,
          })
        }

        return document
      })
    },
  },
})
